import React, { useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import ToastService from "@/service/toaster.service";
import { isBusinessEmail } from "@/helpers/email.helper";
import vendorAuthService from "@/service/auth.service/vendor.auth.service";
import { useDispatch } from 'react-redux';
import { setAuthInfo } from '@/redux/auth/auth.slice';

function SignUp() {
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });

  const [passwordValidations, setPasswordValidations] = useState({
    length: false,
    complexity: false,
    strength: "Weak",
  });

  const dispatch = useDispatch();
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value.trim().replace(/\s+/g, " ");
    setUserData({ ...userData, [name]: updatedValue });

    if (name === "email") {
      setEmailError("");
      if (updatedValue && !isBusinessEmail(updatedValue)) {
        setEmailError("Please enter a valid business email address.");
      }
    }

    if (name === "firstName") setFirstNameError("");
    if (name === "lastName") setLastNameError("");
  };

  const validatePassword = (password) => {
    const lengthValid = password.length >= 8;
    const complexityValid =
      /[a-zA-Z]/.test(password) && /\d/.test(password) && /[~!@#$%^&*()_+={}\[\]:;"'<>,.?/\\|-]/.test(password);

    let strength = "Weak";
    if (lengthValid && complexityValid) {
      strength = "Strong";
    } else if (lengthValid) {
      strength = "Medium";
    }

    setPasswordValidations({
      length: lengthValid,
      complexity: complexityValid,
      strength: strength,
    });
  };

  const handleContinue = () => {
    let valid = true;

    if (!userData.firstName) {
      setFirstNameError("First name is required");
      valid = false;
    }

    if (!userData.lastName) {
      setLastNameError("Last name is required");
      valid = false;
    }

    if (!userData.email || emailError) {
      valid = false;
    }

    if (!userData.password || !passwordValidations.length || !passwordValidations.complexity) {
      valid = false;
    }

    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!handleContinue()) return;

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("first_name", userData.firstName);
      formData.append("last_name", userData.lastName);
      formData.append("email", userData.email.toLowerCase());
      formData.append("password", userData.password);

      const registerResponse = await vendorAuthService.signUp(formData);

      if (registerResponse?.response?.data?.status === false) {
        ToastService.warning(registerResponse?.response?.data?.data?.error);
        return;
      }

      // if (registerResponse?.status === true) {
      //   // Passing email in state to navigate to verify email screen
      //   navigate("/vendor/auth/verify-email-otp", { state: { email: userData.email } });
      //   setStep(2);
      // }
// if (registerResponse?.status === true) {
      //   // Passing email in state to navigate to verify email screen
      //   navigate("/vendor/auth/verify-email-otp", { state: { email: userData.email } });
      //   setStep(2);
      // }
      if (registerResponse.status) {
        const { access_token, data:vendor } = registerResponse.data; // adjust according to your backend response structure
      
        // 1. Set access token & role in localStorage
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('role', 'vendor');
      
        // 2. Set auth info in Redux
        dispatch(setAuthInfo({
          id: vendor._id,
          role: 'vendor',
          email: vendor.email,
          first_name: vendor.first_name,
          last_name: vendor.last_name,
          profile_picture: vendor.profile_picture,
        }));
      
        // 3. Navigate to upload profile
        localStorage.setItem('in_progress_onboarding',true);
        // setSuccessMessage('OTP Verified Successfully!');
        navigate('/vendor/onboarding/upload-profile-image', {
          state: { email:vendor.email },
        });
        }
    } catch (error) {
      console.error("Error catch during registration:", error);
      const message = error?.response?.data?.data?.error || "Something went wrong. Please try again.";
      ToastService.error(message);
    } finally {
      setLoading(false);
    }
  };



  return (
    <Box display="flex" flexDirection={isMobile ? "column" : "row"}>

      <Paper className="side-card" elevation={5}>
        <Typography variant="h5" fontWeight={"bold"} className="title">
          Sign up
        </Typography>

        <form className="signup-form" onSubmit={handleSubmit}>
          <div className="name-fields">
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              value={userData.firstName}
              onChange={handleInputChange}
              error={!!firstNameError}
              helperText={firstNameError}
            />
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={userData.lastName}
              onChange={handleInputChange}
              error={!!lastNameError}
              helperText={lastNameError}
            />
          </div>

          <TextField
            fullWidth
            margin="normal"
            label="Email"
            name="email"
            value={userData.email}
            onChange={handleInputChange}
            error={!!emailError}
            helperText={emailError}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={userData.password}
            onChange={(e) => {
              handleInputChange(e);
              validatePassword(e.target.value);
            }}
            InputProps={{
              endAdornment: (
                <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />

          <Box className="password-validation">
            <Typography variant="body2" className="validationtext">
              <span style={{ color: passwordValidations.length ? "green" : "red" }}>
                {passwordValidations.length ? "✓" : "✗"}
              </span>{" "}
              At least 8 characters
            </Typography>

            <Typography variant="body2" className="validationtext">
              <span style={{ color: passwordValidations.complexity ? "green" : "red" }}>
                {passwordValidations.complexity ? "✓" : "✗"}
              </span>{" "}
              Letters, numbers & special characters
            </Typography>

            <Typography variant="body2" className="validationtext">
              <span
                style={{
                  color:
                    passwordValidations.strength === "Strong"
                      ? "green"
                      : passwordValidations.strength === "Medium"
                        ? "orange"
                        : "red",
                }}
              >
                ✓
              </span>{" "}
              Password Strength: {passwordValidations.strength}
            </Typography>
          </Box>

          <FormControlLabel
            sx={{ ml: -5 }}
            control={
              <Checkbox
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                name="terms"
                color="primary"
                size="small"
              />
            }
            label={
              <span style={{ fontSize: "12px" }}>
                I agree to the{" "}
                <Link to="/terms" target="_blank" style={{ textDecoration: "underline" }}>
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy-policy" target="_blank" style={{ textDecoration: "underline" }}>
                  Privacy Policy.
                </Link>
              </span>
            }
          />

          <Button
            variant="contained"
            fullWidth
            className="btn-login"
            sx={{ mt: 2, color: "black", fontWeight: "bold" }}
            type="submit"
            disabled={
              loading ||
              !agreedToTerms ||
              !passwordValidations.length ||
              !passwordValidations.complexity ||
              !!emailError ||
              !!lastNameError ||
              !!firstNameError ||
              !userData.firstName ||
              !userData.lastName ||
              !userData.email ||
              !userData.password
            }
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Register"}
          </Button>
        </form>
        <Typography variant="body2" align="center" sx={{ mt: 2 }}>
          Already have an account?{' '}
          <Link to="/vendor/auth/login" style={{ textDecoration: "none" }}>
            Log In
          </Link>
        </Typography>
      </Paper>
    </Box>
  );
}

export default SignUp;
